import { useEffect, useMemo, useRef, useState } from 'react';
import { App } from '@capacitor/app';
import { useFindProximity } from '../contexts/FindProximityContext';
import { useCurrentUserProfile } from '../contexts/CurrentUserProfileContext';
import { useEventDetail } from '../hooks/queries/useEventDetail';
import { useEventLineup, useMyArtistVotes } from '../hooks/queries/useEventLineup';
import { useGroupsMembers } from '../hooks/queries/useGroupsMembers';
import { findWatchedPeople, sharingPeopleIds } from '../lib/proximity/findSharingCount';
import { buildCardState, demoCardState, nextChangeAt } from '../lib/liveActivity/cardState';
import {
  LIVE_ACTIVITY_SUPPORTED,
  endLiveActivity,
  syncLiveActivity,
} from '../lib/native/priiLiveActivity';

/** A build made with VITE_LIVE_ACTIVITY_DEMO=1 (or =show, a concert) shows a sample card whatever is on (checking the native side). */
const DEMO_KIND = import.meta.env.VITE_LIVE_ACTIVITY_DEMO;
const DEMO = DEMO_KIND === '1' || DEMO_KIND === 'show';

/** Coalesce bursts (peers settle a few times a second while Find warms up) into one card update. */
const SYNC_DEBOUNCE_MS = 2000;

/**
 * Keeps the lock screen card (Stage 6) in step with the Find session: on while an event of yours is live, the same
 * window Find shares your location in (3 h before to after it ends), off when it ends or you sign out. Renders nothing.
 *
 * iOS only starts a card while Prii is in front, so the first open on the day starts it; from then on the location
 * session keeps the app running in the background, and updates go out from here - when a set starts or ends, when a
 * friend arrives, and each hour while the next set is more than an hour off. Mounted inside FindProximityProvider.
 */
export default function LiveActivityDriver() {
  return LIVE_ACTIVITY_SUPPORTED ? <Driver /> : null;
}

function Driver() {
  const { session, authReady } = useCurrentUserProfile();
  const userId = session?.user?.id ?? null;
  const { sessionEventId, sessionEventRootId, sessionGroupIds, sessionLoading, soloEvent, peers } =
    useFindProximity();
  const eventId = userId ? sessionEventId : null;
  const rootId = userId ? (sessionEventRootId ?? sessionEventId) : null;

  const { data: detail } = useEventDetail(eventId);
  const event = detail ?? (soloEvent?.event_id === eventId ? soloEvent : null);
  const { data: lineup } = useEventLineup(rootId);
  const { data: votes } = useMyArtistVotes(rootId, userId);
  const { data: members } = useGroupsMembers(userId, eventId ? sessionGroupIds : []);

  // the clock: re-render when the card's words next change on their own (a set starts or ends)
  const [tick, setTick] = useState(0);

  const [demoAt] = useState(() => Date.now());
  const card = useMemo(() => {
    if (DEMO) return demoCardState(demoAt, Date.now(), DEMO_KIND === 'show' ? 'show' : 'festival');
    if (!event) return null;
    const now = Date.now();
    const friends = (members ?? []).filter((m) => m.userId !== userId);
    const watched = findWatchedPeople(friends, peers).filter((p) => p.userId !== userId);
    const hereIds = sharingPeopleIds(
      peers,
      friends.map((m) => m.userId),
      now,
    ).filter((id) => id !== userId);
    const byId = new Map(watched.map((p) => [p.userId, p]));
    const faces = hereIds.map((id) => byId.get(id)).filter(Boolean);
    // those with a photo first: initials are the fallback
    faces.sort((a, b) => Number(Boolean(b.photoUrl)) - Number(Boolean(a.photoUrl)));
    return buildCardState({
      event,
      rootEventId: rootId,
      lineup: lineup ?? [],
      votes: votes ?? [],
      here: hereIds.length,
      of: watched.length,
      faces,
      now,
    });
  }, [event, rootId, lineup, votes, members, peers, userId, tick, demoAt]); // eslint-disable-line react-hooks/exhaustive-deps -- tick is the clock

  useEffect(() => {
    const times = card ? [nextChangeAt(card.state), card.overAt].filter((t) => t > Date.now()) : [];
    const at = times.length ? Math.min(...times) : null;
    if (at == null) return undefined;
    const t = setTimeout(() => setTick((n) => n + 1), Math.max(1000, at - Date.now() + 500));
    return () => clearTimeout(t);
  }, [card]);

  // Send it when it says something new. The key leaves out nothing the card draws.
  const key = card ? JSON.stringify(card) : null;
  const sentRef = useRef(null);
  const [resume, setResume] = useState(0);
  useEffect(() => {
    if (!card) return undefined;
    const t = setTimeout(async () => {
      const r = await syncLiveActivity(card.eventId, card.state);
      sentRef.current = r?.ok ? key : null; // a failed start (in the background) goes again on the next open
    }, SYNC_DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [key, resume]); // eslint-disable-line react-hooks/exhaustive-deps -- key stands for card

  // Coming to the front: start it if a background start failed.
  useEffect(() => {
    const p = App.addListener('appStateChange', ({ isActive }) => {
      if (isActive && sentRef.current == null) setResume((n) => n + 1);
    });
    return () => {
      p.then((h) => h.remove()).catch(() => {});
    };
  }, []);

  // Over, or signed out: take it down. Not while the session is still working out what is on (a cold start).
  const settled = authReady && !sessionLoading;
  useEffect(() => {
    if (!settled || eventId || DEMO) return;
    sentRef.current = null;
    void endLiveActivity();
  }, [settled, eventId]);

  return null;
}
